/**
 * Widget: Saúde dos Saldos / Fidelização
 * --------------------------------------
 * Visível apenas para ADMIN. Mostra estado em tempo real do processo de
 * reconciliação automática, alertas e botão para forçar reconciliação.
 *
 * Endpoints: GET /api/admin/reconciliacao-saldos/status
 *            POST /api/admin/reconciliacao-saldos/executar?forcar_todos=true
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Activity, ShieldCheck, ShieldAlert, ShieldX,
  RefreshCw, AlertTriangle, CheckCircle2, Database, Clock, Users,
} from 'lucide-react';
import api from '../services/api';

const ESTADO_CONFIG = {
  OK: { label: 'OK', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: ShieldCheck },
  ATENCAO: { label: 'Atenção', cls: 'text-amber-700 bg-amber-50 border-amber-200', Icon: ShieldAlert },
  ERRO: { label: 'Erro', cls: 'text-red-700 bg-red-50 border-red-200', Icon: ShieldX },
};

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `há ${Math.floor(diff)}s`;
    if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
    return `há ${Math.floor(diff / 86400)} dias`;
  } catch {
    return '—';
  }
}

export default function SaudeSaldosCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const intervalRef = useRef(null);

  // 🛡️ Só admin chama este endpoint. Para outros perfis, o card auto-esconde
  // sem fazer pedidos (evita expor erros de backend a colaboradores quando o
  // ingress devolve HTML, p. ex. Cloudflare 520).
  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return u && u.role === 'admin';
    } catch {
      return false;
    }
  })();

  const fetchStatus = useCallback(async () => {
    if (!isAdmin) {
      setStatus({ _forbidden: true });
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/admin/reconciliacao-saldos/status');
      // Defesa extra: se o response não for JSON válido (e.g. HTML do Cloudflare
      // a ser parseado como string), `data` será string em vez de objecto.
      if (!data || typeof data !== 'object') {
        setStatus({ _forbidden: true });
        return;
      }
      setStatus(data);
      setErro(null);
    } catch (e) {
      const httpStatus = e?.response?.status;
      // Esconder silenciosamente em qualquer caso onde o utilizador não pode
      // fazer nada: 403 (não-admin), 5xx (problema no servidor/ingress),
      // 520-524 (Cloudflare), ou network down. Só mostrar `erro` em produção
      // se for uma resposta REAL do nosso backend (4xx com detail estruturado).
      const isCloudflareEdge = httpStatus >= 520 && httpStatus <= 524;
      const isServerSide = httpStatus >= 500;
      const isForbidden = httpStatus === 403;
      const isNetwork = !e?.response;
      if (isForbidden || isServerSide || isCloudflareEdge || isNetwork) {
        setStatus({ _forbidden: true });
        setErro(null);
      } else {
        // 4xx específico com detail JSON — mostrar mensagem amigável.
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string' && detail.length < 200
          ? detail
          : 'Erro ao obter status';
        setErro(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 60_000); // refresh 60s
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  const executar = async () => {
    setExecuting(true);
    setResultado(null);
    try {
      const { data } = await api.post('/admin/reconciliacao-saldos/executar', null, {
        params: { limite: 1000, forcar_todos: true },
      });
      setResultado(data);
      await fetchStatus();
    } catch (e) {
      setResultado({ ok: false, erro: e?.response?.data?.detail || e?.message || 'Erro' });
    } finally {
      setExecuting(false);
      setConfirmOpen(false);
    }
  };

  if (!isAdmin) return null;
  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="saude-saldos-card">
        <CardContent className="p-4 flex items-center gap-3 text-slate-500">
          <Activity className="h-4 w-4 animate-pulse" />
          <span className="text-sm">A carregar saúde dos saldos…</span>
        </CardContent>
      </Card>
    );
  }
  if (status?._forbidden) return null;
  if (erro) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="saude-saldos-card">
        <CardContent className="p-4 text-sm text-red-700">
          <AlertTriangle className="inline h-4 w-4 mr-2" />
          {erro}
        </CardContent>
      </Card>
    );
  }
  if (!status) return null;

  const cfg = ESTADO_CONFIG[status.estado] || ESTADO_CONFIG.OK;
  const { Icon } = cfg;
  const ulRec = status.ultima_reconciliacao;
  const ulCiclo = status.ultimo_ciclo;
  const drift = status.clientes_com_drift_amostra || {};
  const alertas = status.alertas || [];
  const xd = status.xd_rfid || {};

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="saude-saldos-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-indigo-600" />
            Saúde dos Saldos / Fidelização
          </CardTitle>
          <Badge className={`${cfg.cls} border px-2 py-0.5 text-xs flex items-center gap-1`} data-testid="saude-saldos-estado">
            <Icon className="h-3.5 w-3.5" />
            {cfg.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Linha de estado geral */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="rounded border border-slate-200 p-2 bg-slate-50">
            <div className="text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Último ciclo</div>
            <div className="font-semibold text-slate-800 mt-0.5" data-testid="saude-ultimo-ciclo">
              {ulCiclo ? formatRelative(ulCiclo.executado_em) : '—'}
            </div>
            {ulCiclo && (
              <div className="text-slate-500">{ulCiclo.clientes_corrigidos} corrigido(s)</div>
            )}
          </div>
          <div className="rounded border border-slate-200 p-2 bg-slate-50">
            <div className="text-slate-500 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Última reconciliação</div>
            <div className="font-semibold text-slate-800 mt-0.5" data-testid="saude-ultima-reconc">
              {ulRec ? formatRelative(ulRec.data) : '—'}
            </div>
            {ulRec && (
              <div className="text-slate-500 truncate" title={ulRec.cliente_nome || ''}>
                {ulRec.cliente_nome || '—'}
              </div>
            )}
          </div>
          <div className="rounded border border-slate-200 p-2 bg-slate-50">
            <div className="text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> Clientes activos</div>
            <div className="font-semibold text-slate-800 mt-0.5" data-testid="saude-total-clientes">{status.total_clientes}</div>
            <div className="text-slate-500">{status.reconciliacoes_ultimas_24h} corr. 24h</div>
          </div>
          <div className="rounded border border-slate-200 p-2 bg-slate-50">
            <div className="text-slate-500 flex items-center gap-1"><Database className="h-3 w-3" /> Backup</div>
            <div className={`font-semibold mt-0.5 ${status.backup_28_02_2026_existe ? 'text-emerald-700' : 'text-red-700'}`} data-testid="saude-backup">
              {status.backup_28_02_2026_existe ? 'OK' : 'Em falta'}
            </div>
          </div>
        </div>

        {/* Linha de drift e nunca-reconciliados */}
        <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
          <span>
            Pendentes reconc.: <strong className={drift.com_drift > 0 ? 'text-amber-700' : 'text-emerald-700'} data-testid="saude-drift-actual">{drift.com_drift}/{drift.amostra}</strong>
          </span>
          <span>
            Nunca reconciliados: <strong>{status.clientes_nunca_reconciliados}</strong>
          </span>
          <span>
            Reconc. última hora: <strong>{status.reconciliacoes_ultima_hora}</strong>
          </span>
          {xd.clientes_sem_cartao !== undefined && (
            <span>
              Clientes sem cartão: <strong className={xd.clientes_sem_cartao > 30 ? 'text-amber-700' : 'text-slate-700'} data-testid="saude-sem-cartao">{xd.clientes_sem_cartao}</strong>
            </span>
          )}
          {xd.xd_clientes_total !== undefined && (
            <span>
              XD com cartão: <strong data-testid="saude-xd-com-cartao">{xd.xd_clientes_com_cartao}/{xd.xd_clientes_total}</strong>
            </span>
          )}
        </div>

        {/* Alertas */}
        {alertas.length > 0 && (
          <div className="space-y-1.5" data-testid="saude-alertas">
            {alertas.map((a, i) => (
              <div
                key={i}
                className={`text-xs rounded border p-2 flex gap-2 items-start ${
                  a.nivel === 'error'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold uppercase">{a.tipo}</div>
                  <div className="mt-0.5">{a.mensagem}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resultado da última execução manual */}
        {resultado && (
          <div className="text-xs rounded border p-2 bg-indigo-50 border-indigo-200 text-indigo-800" data-testid="saude-resultado">
            {resultado.ok ? (
              <>
                <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />
                Execução manual concluída: lidos={resultado.sumario?.lidos}, corrigidos={resultado.sumario?.corrigidos}, fantasmas={resultado.sumario?.fantasmas}, erros={resultado.sumario?.erros}
              </>
            ) : (
              <>❌ {resultado.erro || 'Erro desconhecido'}</>
            )}
          </div>
        )}

        {/* Acção (apenas reconciliação manual; sincronização XD é automática) */}
        {!confirmOpen ? (
          <Button
            size="sm"
            variant="outline"
            disabled={executing}
            onClick={() => setConfirmOpen(true)}
            data-testid="saude-btn-forcar"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${executing ? 'animate-spin' : ''}`} />
            Forçar reconciliação agora
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs">
            <span className="text-amber-900">
              Confirmar reconciliação imediata de todos os clientes? (operação idempotente)
            </span>
            <Button size="sm" variant="default" disabled={executing} onClick={executar} data-testid="saude-btn-confirmar" className="bg-emerald-600 hover:bg-emerald-700">
              {executing ? 'A executar…' : 'Confirmar'}
            </Button>
            <Button size="sm" variant="ghost" disabled={executing} onClick={() => setConfirmOpen(false)} data-testid="saude-btn-cancelar">
              Cancelar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
